/**
 * Enum-derived option catalogs + display labels for the automation-rule builder
 * (SE-5506).
 *
 * Every option list is built from `Object.values(<enum>)`, so a value added to
 * the GraphQL schema (and regenerated into `generated/graphql.ts`) appears in
 * the builder automatically — the UI never hardcodes a subset. Labels come from
 * an exhaustive `Record`, so a NEW enum value is a compile error here rather
 * than a silent raw-enum leak into a dropdown.
 * @module features/player-kanban/utils/automationRuleOptions
 */

import {
  PipelineAutomationActionType,
  PipelineAutomationComparator,
  PipelineAutomationConditionType,
  PipelineAutomationTriggerType,
} from "@/generated/graphql";

/** A single dropdown option: the raw enum value + its human label. */
export interface AutomationRuleOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

/** How a condition's value is captured — drives the builder field + preview. */
export type ConditionValueKind = "numeric" | "text" | "stage";

/** Human labels for each trigger. Exhaustive over the enum. */
const TRIGGER_LABELS: Record<PipelineAutomationTriggerType, string> = {
  [PipelineAutomationTriggerType.CardAddedToPipeline]:
    "A card is added to the pipeline",
  [PipelineAutomationTriggerType.CardEnteredStage]: "A card enters a stage",
  [PipelineAutomationTriggerType.ConditionBecameTrue]:
    "A condition becomes true",
  [PipelineAutomationTriggerType.TimeInStageExceeded]:
    "Time in a stage exceeds a limit",
  [PipelineAutomationTriggerType.PeriodicWeekly]: "Every week",
};

/**
 * Actions the builder deliberately does not offer yet.
 *
 * `ASSIGN` (SE-5628) is writable but **not readable**: both
 * `CreatePipelineAutomationRuleInput` and `UpdatePipelineAutomationRuleInput`
 * accept `assignedToUserId`, but the `PipelineAutomationRule` type does not
 * return it. So a rule could be created, but reopening it to edit would show an
 * empty member picker and saving would drop the assignee the author had chosen
 * — a silent data loss on a round-trip the user has no way to detect.
 *
 * Offering the action with that gap is worse than not offering it, so it is
 * excluded here until the backend exposes the field on the read type. This is
 * NOT a "we forgot" list: `ACTION_LABELS` below stays exhaustive over
 * everything outside it, so a genuinely new enum member is still a compile
 * error demanding a decision, exactly as before.
 */
const UNSUPPORTED_ACTIONS: readonly PipelineAutomationActionType[] = [
  PipelineAutomationActionType.Assign,
];

/**
 * Whether the builder can configure this action end-to-end.
 * @param value - The action enum value.
 * @returns True unless the action is deliberately withheld.
 */
const isSupportedAction = (value: PipelineAutomationActionType): boolean =>
  !UNSUPPORTED_ACTIONS.includes(value);

/**
 * Human labels for each action. Stays exhaustive over the WHOLE enum, including
 * the unsupported ones: the server can return a rule with any action (created
 * before an exclusion, or through the API), and the panel still has to render
 * it. Only {@link ACTION_OPTIONS} is filtered — labelling and offering are
 * separate questions.
 */
const ACTION_LABELS: Record<PipelineAutomationActionType, string> = {
  [PipelineAutomationActionType.AddToReviewQueue]: "Add to the review queue",
  [PipelineAutomationActionType.MoveToStage]: "Move to a stage",
  [PipelineAutomationActionType.ApplyTag]: "Apply a tag",
  [PipelineAutomationActionType.RemoveTag]: "Remove a tag",
  [PipelineAutomationActionType.NotifyPipelineOwner]:
    "Notify the pipeline owner",
  [PipelineAutomationActionType.Assign]: "Assign to a member",
};

/** Human labels for each condition type. */
const CONDITION_LABELS: Record<PipelineAutomationConditionType, string> = {
  [PipelineAutomationConditionType.CardInStage]: "Card is in stage",
  [PipelineAutomationConditionType.ContractMonthsRemaining]:
    "Contract months remaining",
  [PipelineAutomationConditionType.DaysInStage]: "Days in stage",
  [PipelineAutomationConditionType.Gpr]: "GPR",
  [PipelineAutomationConditionType.NoteCount]: "Note count",
  [PipelineAutomationConditionType.ReportCount]: "Report count",
  [PipelineAutomationConditionType.Verdict]: "Verdict",
};

/** Human labels for each comparator (used in dropdowns). */
const COMPARATOR_LABELS: Record<PipelineAutomationComparator, string> = {
  [PipelineAutomationComparator.Eq]: "is",
  [PipelineAutomationComparator.Gt]: "is greater than",
  [PipelineAutomationComparator.Gte]: "is at least",
  [PipelineAutomationComparator.Lt]: "is less than",
  [PipelineAutomationComparator.Lte]: "is at most",
};

/**
 * How each condition captures its value:
 *   - `stage` — CARD_IN_STAGE tests occupancy of a phase (no comparator);
 *   - `text` — VERDICT compares a text value;
 *   - `numeric` — everything else compares a decimal threshold.
 */
export const CONDITION_VALUE_KIND: Record<
  PipelineAutomationConditionType,
  ConditionValueKind
> = {
  [PipelineAutomationConditionType.CardInStage]: "stage",
  [PipelineAutomationConditionType.Verdict]: "text",
  [PipelineAutomationConditionType.ContractMonthsRemaining]: "numeric",
  [PipelineAutomationConditionType.DaysInStage]: "numeric",
  [PipelineAutomationConditionType.Gpr]: "numeric",
  [PipelineAutomationConditionType.NoteCount]: "numeric",
  [PipelineAutomationConditionType.ReportCount]: "numeric",
};

/**
 * The comparator a condition starts with, by value kind. A stage-occupancy test
 * has no comparator at all; a text test reads "is"; a numeric threshold reads
 * "is at least".
 *
 * Every site that creates or re-kinds a condition MUST seed through
 * {@link defaultComparatorForConditionType} rather than deciding for itself
 * (SE-6531): the Save gate requires a comparator on a comparator-bearing
 * condition, so a site that seeds `null` produces a row the author can never
 * complete — and the comparator dropdown's placeholder makes it look chosen.
 */
const DEFAULT_COMPARATOR_BY_KIND: Record<
  ConditionValueKind,
  PipelineAutomationComparator | null
> = {
  stage: null,
  text: PipelineAutomationComparator.Eq,
  numeric: PipelineAutomationComparator.Gte,
};

/**
 * The comparator a freshly-seeded condition of this type should carry.
 * @param value - The condition-type enum value.
 * @returns The default comparator, or null for a stage-occupancy condition.
 */
export const defaultComparatorForConditionType = (
  value: PipelineAutomationConditionType
): PipelineAutomationComparator | null =>
  DEFAULT_COMPARATOR_BY_KIND[CONDITION_VALUE_KIND[value]];

/**
 * Build an option list from an enum object + a label record. Order follows the
 * enum's declaration order (as returned by `Object.values`).
 * @param enumObject - The generated string-enum object.
 * @param labels - Exhaustive value → label record.
 * @returns The option list, in enum order.
 */
const toOptions = <T extends string>(
  enumObject: Record<string, T>,
  labels: Record<T, string>
): readonly AutomationRuleOption<T>[] =>
  Object.values(enumObject).map(value => ({ value, label: labels[value] }));

/** All triggers, in enum order — rendered by the Automation-mode trigger picker. */
export const TRIGGER_OPTIONS: readonly AutomationRuleOption<PipelineAutomationTriggerType>[] =
  toOptions(PipelineAutomationTriggerType, TRIGGER_LABELS);

/**
 * Offered actions, in enum order — rendered by the Automation-mode action
 * picker. Filtered to the actions the builder can configure end-to-end; see
 * {@link UNSUPPORTED_ACTIONS} for why one is withheld.
 */
export const ACTION_OPTIONS: readonly AutomationRuleOption<PipelineAutomationActionType>[] =
  Object.values(PipelineAutomationActionType)
    .filter(isSupportedAction)
    .map(value => ({ value, label: ACTION_LABELS[value] }));

/** All condition types, in enum order — rendered by the condition-type picker. */
export const CONDITION_TYPE_OPTIONS: readonly AutomationRuleOption<PipelineAutomationConditionType>[] =
  toOptions(PipelineAutomationConditionType, CONDITION_LABELS);

/** All comparators, in enum order — rendered by the comparator picker. */
export const COMPARATOR_OPTIONS: readonly AutomationRuleOption<PipelineAutomationComparator>[] =
  toOptions(PipelineAutomationComparator, COMPARATOR_LABELS);

/**
 * Display label for a trigger value.
 * @param value - The trigger enum value.
 * @returns The human label.
 */
export const triggerLabel = (value: PipelineAutomationTriggerType): string =>
  TRIGGER_LABELS[value];

/**
 * Display label for an action value.
 * @param value - The action enum value.
 * @returns The human label.
 */
export const actionLabel = (value: PipelineAutomationActionType): string =>
  ACTION_LABELS[value];

/**
 * Display label for a condition type.
 * @param value - The condition-type enum value.
 * @returns The human label.
 */
export const conditionTypeLabel = (
  value: PipelineAutomationConditionType
): string => CONDITION_LABELS[value];

/**
 * Display label for a comparator.
 * @param value - The comparator enum value.
 * @returns The human label.
 */
export const comparatorLabel = (value: PipelineAutomationComparator): string =>
  COMPARATOR_LABELS[value];

/**
 * True when a trigger requires a stage to be chosen (the `triggerKanbanPhaseId`
 * field). Both CARD_ENTERED_STAGE and TIME_IN_STAGE_EXCEEDED watch a stage.
 * @param value - The trigger enum value.
 * @returns Whether the trigger needs a stage.
 */
export const triggerNeedsStage = (
  value: PipelineAutomationTriggerType
): boolean =>
  value === PipelineAutomationTriggerType.CardEnteredStage ||
  value === PipelineAutomationTriggerType.TimeInStageExceeded;

/**
 * True when an action targets a stage (`targetKanbanPhaseId`).
 * @param value - The action enum value.
 * @returns Whether the action needs a target stage.
 */
export const actionNeedsTargetStage = (
  value: PipelineAutomationActionType
): boolean =>
  value === PipelineAutomationActionType.MoveToStage ||
  value === PipelineAutomationActionType.AddToReviewQueue;

/**
 * True when an action targets a tag (`kanbanTagId`).
 * @param value - The action enum value.
 * @returns Whether the action needs a tag.
 */
export const actionNeedsTag = (value: PipelineAutomationActionType): boolean =>
  value === PipelineAutomationActionType.ApplyTag ||
  value === PipelineAutomationActionType.RemoveTag;

/**
 * True when an action can carry a terminal outcome (`requestedOutcome`). Only a
 * move-shaped action does — NOT the review-queue action, which also targets a
 * stage but never surfaces an outcome. Shared by the builder's `showOutcome`
 * gate and the input mapper so an outcome can never leak onto a review-queue
 * action or a Guard (which folds to ADD_TO_REVIEW_QUEUE).
 * @param value - The action enum value.
 * @returns Whether the action can carry an outcome.
 */
export const actionSupportsOutcome = (
  value: PipelineAutomationActionType
): boolean => value === PipelineAutomationActionType.MoveToStage;
